import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import boleumlogo from "../../src/assets/boleumlogo.png";
import useAuthStore from "../../src/stores/useAuthStore";
import useCourseStore from '../../src/stores/useCourseStore';
import { useToast } from './ToastContainer';
import NotificationBell from './NotificationBell';


const LMSHeader = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const { logout } = useAuthStore();
  const isAuthenticated = useAuthStore(state => state.user);
  const user = useAuthStore(state => state.user);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  const { searchCourses, searchResults, fetchCategories, categories: allCategories, isLoading } = useCourseStore();
  const [oilAndGasRoot, setOilAndGasRoot] = useState(null);

  const [showResults, setShowResults] = useState(false);
  const searchRef = useRef(null);

  const handleLogout = async () => {
    try {
      await logout();
      toast.success('You’ve been logged out successfully 👋');
    } catch (error) {
      toast.error('Logout failed. Please try again.');
    }
  };

  const renderCategoryList = useCallback((cats = [], depth = 0, limitPerLevel = 20) => {
    if (!Array.isArray(cats) || cats.length === 0) return null;

    return cats.slice(0, limitPerLevel).map((cat) => {
      const key = cat._id || cat.id || cat.slug || `${cat.name}-${depth}`;
      const slug = cat.slug || cat._id;

      return (
        <li key={key}>
          <Link
            to={`/categories/${slug}`}
            className={`block text-sm ${depth === 0
              ? 'text-neutral-600 dark:text-neutral-400 hover:text-primary-600 dark:hover:text-primary-400'
              : 'text-xs text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-300'
              }`}
          >
            {Array(depth).fill('— ').join('')}
            {cat.name}
          </Link>

          {cat.children && cat.children.length > 0 && (
            <ul className="pl-3 mt-1">
              {renderCategoryList(cat.children, depth + 1, limitPerLevel)}
            </ul>
          )}
        </li>
      );
    });
  }, []);

  useEffect(() => {
    const loadCategories = async () => {
      await fetchCategories();
    };
    loadCategories();
  }, [fetchCategories]);

  useEffect(() => {
    if (allCategories.length > 0) {
      const root = allCategories.find(
        (cat) =>
          cat.name.toLowerCase() === 'oil and gas courses' ||
          cat.slug === 'oil-and-gas-courses'
      );
      if (root) setOilAndGasRoot(root);
    }
  }, [allCategories]);

  // Close results when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowResults(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Search as user types
  useEffect(() => {
    if (searchQuery.trim().length > 0) {
      const timer = setTimeout(() => {
        searchCourses(searchQuery);
        setShowResults(true);
      }, 300);
      return () => clearTimeout(timer);
    } else {
      setShowResults(false);
    }
  }, [searchQuery, searchCourses]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/courses?search=${encodeURIComponent(searchQuery)}`);
    }
  };

  useEffect(() => {
    const savedMode = localStorage.getItem('darkMode') === 'true';
    setIsDarkMode(savedMode);
    if (savedMode) {
      document.documentElement.classList.add('dark');
    }
  }, []);

  const toggleDarkMode = () => {
    const newMode = !isDarkMode;
    setIsDarkMode(newMode);
    localStorage.setItem('darkMode', newMode);
    if (newMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  };

  const toggleMobileMenu = () => setIsMobileMenuOpen(!isMobileMenuOpen);
  const toggleUserMenu = () => setIsUserMenuOpen(!isUserMenuOpen);
  const toggleSearch = () => setIsSearchOpen(!isSearchOpen);

  return (
    <header className={`bg-white dark:bg-neutral-900 shadow-sm border-b border-neutral-200 dark:border-neutral-700 sticky top-0 z-50 transition-colors duration-200`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">

          {/* LEFT: Auth Actions (Desktop Only) */}
          <div className="hidden md:flex items-center space-x-4">
            {isAuthenticated ? (
              <>
                <Link
                  to={`/${user?.role === "instructor" ? "instructor/dashboard" : user?.role === "admin" ? "admin/cloud/dashboard" : "student/dashboard"}`}
                  className="px-4 py-2 text-sm font-medium text-neutral-700 dark:text-neutral-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                >
                  Dashboard
                </Link>
                <button
                  onClick={handleLogout}
                  className="text-sm font-medium text-danger-600 dark:text-danger-400 hover:underline"
                >
                  Logout
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  className="text-sm font-medium text-neutral-700 dark:text-neutral-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                >
                  Log In
                </Link>
                <Link
                  to="/register"
                  className="text-sm font-medium text-neutral-700 dark:text-neutral-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                >
                  Register
                </Link>
                <Link
                  to="/register"
                  state={{ userType: "instructor" }}
                  className="px-4 py-2 text-primary-500 hover:text-primary-600 text-sm font-medium rounded-lg  transition-all duration-200"
                >
                  Become an instructor
                </Link>
              </>
            )}
          </div>

          <div className="flex-shrink-0 flex items-center md:mx-0 space-x-4">
            <Link to="/" className="flex items-center space-x-2">
              {/* Logo Image */}
              <img
                src={boleumlogo}
                alt="BoleumLearn Logo"
                className="h-8 w-8 object-contain"
              />

              <span className="text-2xl font-bold text-primary-600 dark:text-primary-400">
                BoleumLearn
              </span>
            </Link>

            {/* Desktop Nav */}
            <nav className="hidden md:flex md:space-x-6">
              <div className="relative group">
                <button className="flex items-center text-sm font-medium text-neutral-700 dark:text-neutral-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors focus:outline-none">
                  Courses
                  <svg className="ml-1 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {/* MEGA MENU DROPDOWN */}
                <div className="absolute left-0 top-full mt-2 w-96 bg-white dark:bg-neutral-800 rounded-lg shadow-xl border border-neutral-200 dark:border-neutral-700 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                  <div className="flex">
                    {/* Image Panel */}
                    <div className="w-1/2 p-4 bg-gradient-to-br from-primary-50 to-primary-100 dark:from-primary-900/20 dark:to-primary-800/10 rounded-l-lg">
                      <p className="mt-3 text-xs text-primary-700 dark:text-primary-300 font-medium">
                        Explore top Oil & Gas Engineering courses
                      </p>

                      <Link
                        to="/courses"
                        className="block text-sm font-medium text-primary-600 dark:text-primary-400 mt-2"
                      >
                        View All Courses →
                      </Link>
                    </div>
                    {/* Menu Panel */}
                    <div className="w-1/2 p-4 overflow-y-auto max-h-72">
                      <h4 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 mb-3">
                        Oil and Gas Courses
                      </h4>

                      {isLoading ? (
                        <div className="flex items-center justify-center h-24">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-500"></div>
                        </div>
                      ) : (
                        <div>
                          <ul className="space-y-2">
                            {oilAndGasRoot && oilAndGasRoot.children && oilAndGasRoot.children.length > 0 ? (
                              renderCategoryList(oilAndGasRoot.children, 0, 20)
                            ) : (
                              <li className="text-sm text-neutral-500 dark:text-neutral-400">
                                No categories available
                              </li>
                            )}
                          </ul>
                          <div className="mt-4">
                            <Link
                              to="/categories"
                              className="text-sm font-medium text-primary-600 dark:text-primary-400 transition-all duration-200"
                            >
                              View all categories →
                            </Link>
                          </div>
                        </div>
                      )}

                    </div>
                  </div>
                </div>
              </div>

              <Link to="/categories" className="text-sm font-medium text-neutral-700 dark:text-neutral-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors">
                Course Categories
              </Link>

              <Link to="/about" className="text-sm font-medium text-neutral-700 dark:text-neutral-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors">
                About
              </Link>
              <Link to="/contact" className="text-sm font-medium text-neutral-700 dark:text-neutral-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors">
                Contact
              </Link>

              <Link to="/help-support" className="text-sm font-medium text-neutral-700 dark:text-neutral-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors">
                Help & Support
              </Link>
            </nav>

            {/* Desktop Search Toggle */}
            <button
              onClick={toggleSearch}
              className="hidden lg:block p-2 text-neutral-500 dark:text-neutral-400 hover:text-primary-600 dark:hover:text-primary-400 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              aria-label="Search"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </button>
          </div>

          {/* RIGHT: User Actions + Dark Mode + Lang */}
          <div className="hidden md:flex items-center space-x-3">

            {/* Dark Mode Toggle */}
            <button
              onClick={toggleDarkMode}
              className="p-2 text-neutral-500 dark:text-neutral-400 hover:text-primary-600 dark:hover:text-primary-400 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {isDarkMode ? (
                <svg
                  className="w-5 h-5 text-yellow-400"
                  viewBox="0 0 24 24"
                  fill="none"
                  role="img"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <title>Sun — switch to light mode</title>
                  <circle cx="12" cy="12" r="4" fill="currentColor" />
                  <g stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" opacity="0.9">
                    <line x1="12" y1="1.5" x2="12" y2="4.5" />
                    <line x1="12" y1="19.5" x2="12" y2="22.5" />
                    <line x1="1.5" y1="12" x2="4.5" y2="12" />
                    <line x1="19.5" y1="12" x2="22.5" y2="12" />
                    <line x1="4.2" y1="4.2" x2="6.3" y2="6.3" />
                    <line x1="17.7" y1="17.7" x2="19.8" y2="19.8" />
                    <line x1="4.2" y1="19.8" x2="6.3" y2="17.7" />
                    <line x1="17.7" y1="6.3" x2="19.8" y2="4.2" />
                  </g>
                </svg>
              ) : (
                <svg
                  className="w-5 h-5 text-gray-800 dark:text-gray-200"
                  viewBox="0 0 24 24"
                  fill="none"
                  role="img"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <title>Moon — switch to dark mode</title>
                  <path
                    d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z"
                    fill="currentColor"
                    opacity="0.95"
                  />
                </svg>
              )}
            </button>

            {isAuthenticated && (
              <NotificationBell />
            )}

            {/* User Avatar Dropdown */}
            {isAuthenticated && (
              <div className="relative">
                <button
                  onClick={toggleUserMenu}
                  className="flex items-center focus:outline-none focus:ring-2 focus:ring-primary-500 rounded-full"
                  aria-label="User menu"
                >
                  <div className='border-2 rounded-full border-white dark:border-neutral-800'>
                    <img
                      src={`${process.env.REACT_APP_SOCKET_URL}/public${user.avatar}`}
                      alt="Profile"
                      className="w-8 h-8 rounded-full object-cover"
                    />
                  </div>
                </button>

                {isUserMenuOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-neutral-800 rounded-lg shadow-lg border border-neutral-200 dark:border-neutral-700 py-1 z-50">
                    <Link
                      to={`/${user?.role === "instructor" ? "instructor/dashboard" : user?.role === "admin" ? "admin/cloud/dashboard" : "student/dashboard"}`}
                      className="block px-4 py-2 text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700"
                    >
                      Dashboard
                    </Link>

                    {user?.role === "instructor" && (
                      <>
                        <Link
                          to={'/instructor/course/builder'}
                          className="block px-4 py-2 text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700"
                        >
                          Course Builder
                        </Link>

                        <Link
                          to={'/instructor/my-courses'}
                          className="block px-4 py-2 text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700"
                        >
                          My Courses
                        </Link>

                        <Link
                          to={'/instructor/discussions'}
                          className="block px-4 py-2 text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700"
                        >
                          Discussions
                        </Link>

                        <Link
                          to={'/instructor/analytics'}
                          className="block px-4 py-2 text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700"
                        >
                          Analytics
                        </Link>

                        <Link
                          to={'/instructor/certificates'}
                          className="block px-4 py-2 text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700"
                        >
                          Certificates
                        </Link>

                        <Link
                          to={'/instructor/revenue'}
                          className="block px-4 py-2 text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700"
                        >
                          Revenue
                        </Link>

                        <Link
                          to={'/instructor/assignment'}
                          className="block px-4 py-2 text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700"
                        >
                          Assignment
                        </Link>

                        <Link
                          to={'/instructor/notifications'}
                          className="block px-4 py-2 text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700"
                        >
                          Notifications
                        </Link>

                        <Link
                          to={'/instructor/coupons'}
                          className="block px-4 py-2 text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700"
                        >
                          Coupons
                        </Link>

                        <Link
                          to={'/instructor/profile'}
                          className="block px-4 py-2 text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700"
                        >
                          Profile
                        </Link>
                      </>
                    )}

                    {user?.role === "student" && (
                      <>
                        <Link
                          to={'/student/my-courses'}
                          className="block px-4 py-2 text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700"
                        >
                          My Courses
                        </Link>

                        <Link
                          to={'/student/progress'}
                          className="block px-4 py-2 text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700"
                        >
                          Progress
                        </Link>

                        <Link
                          to={'/student/achivements'}
                          className="block px-4 py-2 text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700"
                        >
                          Achivements
                        </Link>

                        <Link
                          to={'/student/wishlist'}
                          className="block px-4 py-2 text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700"
                        >
                          Wishlist
                        </Link>

                        <Link
                          to={'/student/notifications'}
                          className="block px-4 py-2 text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700"
                        >
                          Notifications
                        </Link>

                        <Link
                          to={'/student/billings'}
                          className="block px-4 py-2 text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700"
                        >
                          Billings
                        </Link>

                        <Link
                          to={'/student/profile'}
                          className="block px-4 py-2 text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700"
                        >
                          Profile
                        </Link>
                      </>
                    )}

                    {user?.role === "admin" && (
                      <>
                        <Link
                          to={'/admin/cloud/users'}
                          className="block px-4 py-2 text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700"
                        >
                          Users
                        </Link>

                        <Link
                          to={'/admin/cloud/course/builder'}
                          className="block px-4 py-2 text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700"
                        >
                          Course Builder
                        </Link>

                        <Link
                          to={'/admin/cloud/courses'}
                          className="block px-4 py-2 text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700"
                        >
                          Courses
                        </Link>

                        <Link
                          to={'/admin/cloud/certificates'}
                          className="block px-4 py-2 text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700"
                        >
                          Certificates
                        </Link>

                        <Link
                          to={'/admin/cloud/categories'}
                          className="block px-4 py-2 text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700"
                        >
                          Categories
                        </Link>

                        <Link
                          to={'/admin/cloud/withdrawal'}
                          className="block px-4 py-2 text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700"
                        >
                          Withdrawals
                        </Link>

                        <Link
                          to={'/admin/cloud/analytics'}
                          className="block px-4 py-2 text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700"
                        >
                          Analytics
                        </Link>

                        <Link
                          to={'/admin/cloud/coupons'}
                          className="block px-4 py-2 text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700"
                        >
                          Coupons
                        </Link>

                        <Link
                          to={'/admin/cloud/notifications'}
                          className="block px-4 py-2 text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700"
                        >
                          Notifications
                        </Link>

                        <Link
                          to={'/admin/cloud/settings'}
                          className="block px-4 py-2 text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700"
                        >
                          Settings
                        </Link>
                      </>
                    )}

                    <hr className="my-1 border-neutral-200 dark:border-neutral-700" />
                    <button
                      onClick={handleLogout}
                      className="block w-full text-left px-4 py-2 text-sm text-danger-600 dark:text-danger-400 hover:bg-neutral-100 dark:hover:bg-neutral-700"
                    >
                      Sign out
                    </button>
                  </div>
                )}
              </div>
            )}

          </div>

          {/* MOBILE: Hamburger + Search Toggle */}
          <div className="flex md:hidden items-center space-x-2">
            <button
              onClick={toggleSearch}
              className="p-2 text-neutral-500 hidden dark:text-neutral-400 hover:text-primary-600 dark:hover:text-primary-400 rounded-md focus:outline-none"
              aria-label="Search"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </button>
            <button
              onClick={toggleDarkMode}
              className="p-2 text-neutral-500 dark:text-neutral-400 hover:text-primary-600 dark:hover:text-primary-400 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {isDarkMode ? (
                <svg
                  className="w-5 h-5 text-yellow-400"
                  viewBox="0 0 24 24"
                  fill="none"
                  role="img"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <title>Sun — switch to light mode</title>
                  <circle cx="12" cy="12" r="4" fill="currentColor" />
                  <g stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" opacity="0.9">
                    <line x1="12" y1="1.5" x2="12" y2="4.5" />
                    <line x1="12" y1="19.5" x2="12" y2="22.5" />
                    <line x1="1.5" y1="12" x2="4.5" y2="12" />
                    <line x1="19.5" y1="12" x2="22.5" y2="12" />
                    <line x1="4.2" y1="4.2" x2="6.3" y2="6.3" />
                    <line x1="17.7" y1="17.7" x2="19.8" y2="19.8" />
                    <line x1="4.2" y1="19.8" x2="6.3" y2="17.7" />
                    <line x1="17.7" y1="6.3" x2="19.8" y2="4.2" />
                  </g>
                </svg>
              ) : (
                <svg
                  className="w-5 h-5 text-gray-800 dark:text-gray-200"
                  viewBox="0 0 24 24"
                  fill="none"
                  role="img"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <title>Moon — switch to dark mode</title>
                  <path
                    d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z"
                    fill="currentColor"
                    opacity="0.95"
                  />
                </svg>
              )}
            </button>
            <button
              onClick={toggleMobileMenu}
              className="text-neutral-700 dark:text-neutral-300 hover:text-primary-600 dark:hover:text-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-500 p-2 rounded-md"
              aria-label="Toggle menu"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          </div>
        </div>

        {/* DESKTOP SEARCH BAR (Conditional) */}
        {isSearchOpen && (
          <div className="hidden md:block py-3 border-t border-neutral-200 dark:border-neutral-700 relative" ref={searchRef}>
            <form onSubmit={handleSearchSubmit} className="max-w-md mx-auto">
              <div className="relative">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search courses..."
                  className="w-full px-4 py-2 pl-10 pr-12 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-800 text-neutral-900 dark:text-white placeholder-neutral-500 dark:placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
                <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
            </form>

            {/* Auto-complete Results */}
            {showResults && searchQuery.trim().length > 0 && (
              <div className="absolute left-0 right-0 z-10 mt-1 bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-lg shadow-lg max-h-96 overflow-y-auto">
                {isLoading ? (
                  <div className="p-4 text-center text-neutral-500 dark:text-neutral-400">Searching...</div>
                ) : searchResults.length > 0 ? (
                  searchResults.map((course) => (
                    <div
                      key={course._id}
                      onClick={() => {
                        navigate(`/course/${course.slug}/${course._id}`);
                        setSearchQuery('');
                        setShowResults(false);
                      }}
                      className="p-3 hover:bg-neutral-50 dark:hover:bg-neutral-700/50 cursor-pointer border-b border-neutral-100 dark:border-neutral-700 last:border-b-0"
                    >
                      <div className="font-medium text-neutral-900 dark:text-white">{course.title}</div>
                      <div className="text-sm text-neutral-500 dark:text-neutral-400">{course.category.name} • {course.level}</div>
                      <div className="flex items-center mt-1">
                        <svg className="w-4 h-4 text-yellow-500 mr-1" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                        <span className="text-sm text-neutral-600 dark:text-neutral-400">
                          {course.rating || 0} ({course.reviews?.length || 0} reviews)
                        </span>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="p-4 text-center text-neutral-500 dark:text-neutral-400">No courses found</div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* MOBILE MENU (Conditional Render with Slide Animation) */}
      {isMobileMenuOpen && (
        <div className="md:hidden fixed inset-0 bg-white dark:bg-neutral-900 z-50 overflow-y-auto">
          <div className="px-4 pt-6 pb-20 space-y-6">
            {/* Close Button */}
            <div className="flex justify-between items-center">
              <Link to="/" className="flex items-center space-x-2">
                {/* Logo Image */}
                <img
                  src={boleumlogo}
                  alt="BoleumLearn Logo"
                  className="h-8 w-8 object-contain"
                />

                {/* Brand Text */}
                <span className="text-2xl font-bold text-primary-600 dark:text-primary-400">
                  BoleumLearn
                </span>
              </Link>
              <button
                onClick={toggleMobileMenu}
                className="p-2 text-neutral-500 dark:text-neutral-400 hover:text-primary-600 dark:hover:text-primary-400 rounded-md"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Mobile Search Bar */}
            <form onSubmit={handleSearchSubmit} className="max-w-md mx-auto">
              <div className="relative">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search courses..."
                  className="w-full px-4 py-2 pl-10 pr-12 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-800 text-neutral-900 dark:text-white placeholder-neutral-500 dark:placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
                <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
            </form>

            {/* Auto-complete Results */}
            {showResults && searchQuery.trim().length > 0 && (
              <div className="absolute left-0 right-0 z-10 mt-1 bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-lg shadow-lg max-h-96 overflow-y-auto">
                {isLoading ? (
                  <div className="p-4 text-center text-neutral-500 dark:text-neutral-400">Searching...</div>
                ) : searchResults.length > 0 ? (
                  searchResults.map((course) => (
                    <div
                      key={course._id}
                      onClick={() => {
                        navigate(`/course/${course.slug}/${course._id}`);
                        setSearchQuery('');
                        setShowResults(false);
                      }}
                      className="p-3 hover:bg-neutral-50 dark:hover:bg-neutral-700/50 cursor-pointer border-b border-neutral-100 dark:border-neutral-700 last:border-b-0"
                    >
                      <div className="font-medium text-neutral-900 dark:text-white">{course.title}</div>
                      <div className="text-sm text-neutral-500 dark:text-neutral-400">{course.category.name} • {course.level}</div>
                      <div className="flex items-center mt-1">
                        <svg className="w-4 h-4 text-yellow-500 mr-1" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                        <span className="text-sm text-neutral-600 dark:text-neutral-400">
                          {course.rating || 0} ({course.reviews?.length || 0} reviews)
                        </span>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="p-4 text-center text-neutral-500 dark:text-neutral-400">No courses found</div>
                )}
              </div>
            )}

            {/* Mobile categories list */}
            <div>
              <h4 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 mb-3">
                Oil and Gas Courses
              </h4>
              {isLoading ? (
                <div className="flex items-center justify-center h-24">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-500"></div>
                </div>
              ) : (
                <div>
                  <ul className="space-y-2">
                    {oilAndGasRoot && oilAndGasRoot.children && oilAndGasRoot.children.length > 0 ? (
                      renderCategoryList(oilAndGasRoot.children, 0, 20)
                    ) : (
                      <li className="text-sm text-neutral-500 dark:text-neutral-400">
                        No categories available
                      </li>
                    )}
                  </ul>
                  <div className="mt-4">
                    <Link
                      to="/categories"
                      className="text-sm font-medium text-primary-600 dark:text-primary-400 transition-all duration-200"
                    >
                      View all categories →
                    </Link>
                  </div>
                </div>
              )}
            </div>

            {/* Navigation Links */}
            <div className="space-y-1 flex flex-col">
              <Link to="/categories" className="text-sm font-medium text-neutral-700 dark:text-neutral-300 hover:text-primary-600 dark:hover:text-primary-400 transition-all duration-200">
                Course Categories
              </Link>

              <Link to="/about" className="text-sm font-medium text-neutral-700 dark:text-neutral-300 hover:text-primary-600 dark:hover:text-primary-400 transition-all duration-200">
                About
              </Link>
              <Link to="/contact" className="text-sm font-medium text-neutral-700 dark:text-neutral-300 hover:text-primary-600 dark:hover:text-primary-400 transition-all duration-200">
                Contact
              </Link>

              <Link to="/help-support" className="text-sm font-medium text-neutral-700 dark:text-neutral-300 hover:text-primary-600 dark:hover:text-primary-400 transition-all duration-200">
                Help & Support
              </Link>
            </div>

            {/* Auth Actions */}
            {isAuthenticated ? (
              <div className="pt-4 space-y-3 flex flex-col">
                <Link
                  to={`/${user?.role === "instructor" ? "instructor/dashboard" : user?.role === "admin" ? "admin/cloud/dashboard" : "student/dashboard"}`}
                  className="block px-4 py-2 text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700"
                >
                  Dashboard
                </Link>

                {user?.role === "instructor" && (
                  <>
                    <Link
                      to={'/instructor/course/builder'}
                      className="block px-4 py-2 text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700"
                    >
                      Course Builder
                    </Link>

                    <Link
                      to={'/instructor/my-courses'}
                      className="block px-4 py-2 text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700"
                    >
                      My Courses
                    </Link>

                    <Link
                      to={'/instructor/discussions'}
                      className="block px-4 py-2 text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700"
                    >
                      Discussions
                    </Link>

                    <Link
                      to={'/instructor/analytics'}
                      className="block px-4 py-2 text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700"
                    >
                      Analytics
                    </Link>

                    <Link
                      to={'/instructor/certificates'}
                      className="block px-4 py-2 text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700"
                    >
                      Certificates
                    </Link>

                    <Link
                      to={'/instructor/revenue'}
                      className="block px-4 py-2 text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700"
                    >
                      Revenue
                    </Link>

                    <Link
                      to={'/instructor/assignment'}
                      className="block px-4 py-2 text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700"
                    >
                      Assignment
                    </Link>

                    <Link
                      to={'/instructor/notifications'}
                      className="block px-4 py-2 text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700"
                    >
                      Notifications
                    </Link>

                    <Link
                      to={'/instructor/coupons'}
                      className="block px-4 py-2 text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700"
                    >
                      Coupons
                    </Link>

                    <Link
                      to={'/instructor/profile'}
                      className="block px-4 py-2 text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700"
                    >
                      Profile
                    </Link>
                  </>
                )}

                {user?.role === "student" && (
                  <>
                    <Link
                      to={'/student/my-courses'}
                      className="block px-4 py-2 text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700"
                    >
                      My Courses
                    </Link>

                    <Link
                      to={'/student/progress'}
                      className="block px-4 py-2 text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700"
                    >
                      Progress
                    </Link>

                    <Link
                      to={'/student/achivements'}
                      className="block px-4 py-2 text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700"
                    >
                      Achivements
                    </Link>

                    <Link
                      to={'/student/wishlist'}
                      className="block px-4 py-2 text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700"
                    >
                      Wishlist
                    </Link>

                    <Link
                      to={'/student/notifications'}
                      className="block px-4 py-2 text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700"
                    >
                      Notifications
                    </Link>

                    <Link
                      to={'/student/billings'}
                      className="block px-4 py-2 text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700"
                    >
                      Billings
                    </Link>

                    <Link
                      to={'/student/profile'}
                      className="block px-4 py-2 text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700"
                    >
                      Profile
                    </Link>
                  </>
                )}

                {user?.role === "admin" && (
                  <>
                    <Link
                      to={'/admin/cloud/users'}
                      className="block px-4 py-2 text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700"
                    >
                      Users
                    </Link>

                    <Link
                      to={'/admin/cloud/course/builder'}
                      className="block px-4 py-2 text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700"
                    >
                      Course Builder
                    </Link>

                    <Link
                      to={'/admin/cloud/courses'}
                      className="block px-4 py-2 text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700"
                    >
                      Courses
                    </Link>

                    <Link
                      to={'/admin/cloud/certificates'}
                      className="block px-4 py-2 text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700"
                    >
                      Certificates
                    </Link>

                    <Link
                      to={'/admin/cloud/categories'}
                      className="block px-4 py-2 text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700"
                    >
                      Categories
                    </Link>

                    <Link
                      to={'/admin/cloud/withdrawal'}
                      className="block px-4 py-2 text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700"
                    >
                      Withdrawals
                    </Link>

                    <Link
                      to={'/admin/cloud/analytics'}
                      className="block px-4 py-2 text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700"
                    >
                      Analytics
                    </Link>

                    <Link
                      to={'/admin/cloud/coupons'}
                      className="block px-4 py-2 text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700"
                    >
                      Coupons
                    </Link>

                    <Link
                      to={'/admin/cloud/notifications'}
                      className="block px-4 py-2 text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700"
                    >
                      Notifications
                    </Link>

                    <Link
                      to={'/admin/cloud/settings'}
                      className="block px-4 py-2 text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700"
                    >
                      Settings
                    </Link>
                  </>
                )}


                <hr className="my-1 border-neutral-200 dark:border-neutral-700" />
                <button
                  onClick={handleLogout}
                  className="block w-full text-left px-4 py-2 text-sm text-danger-600 dark:text-danger-400 hover:bg-neutral-100 dark:hover:bg-neutral-700"
                >
                  Sign out
                </button>
              </div>
            ) : (
              <div className="pt-4 space-y-3 flex flex-col">
                <Link
                  to="/login"
                  className="text-sm font-medium text-neutral-700 dark:text-neutral-300 hover:text-primary-600 dark:hover:text-primary-400 transition-all duration-200"
                >
                  Log In
                </Link>
                <Link
                  to="/register"
                  className="text-sm font-medium text-neutral-700 dark:text-neutral-300 hover:text-primary-600 dark:hover:text-primary-400 transition-all duration-200"
                >
                  Register
                </Link>
                <Link
                  to="/register"
                  state={{ userType: "instructor" }}
                  className="text-primary-500 hover:text-primary-600 text-sm font-medium rounded-lg  transition-all duration-200"
                >
                  Become an instructor
                </Link>
              </div>
            )}


            {/* Footer Actions */}
            <div className="flex justify-between items-center pt-6 border-t border-neutral-200 dark:border-neutral-700">
              <div className="flex space-x-4">
                <button
                  onClick={toggleDarkMode}
                  className="p-2 text-neutral-500 dark:text-neutral-400 hover:text-primary-600 dark:hover:text-primary-400 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
                >
                  {isDarkMode ? (
                    <svg
                      className="w-5 h-5 text-yellow-400"
                      viewBox="0 0 24 24"
                      fill="none"
                      role="img"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <title>Sun — switch to light mode</title>
                      <circle cx="12" cy="12" r="4" fill="currentColor" />
                      <g stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" opacity="0.9">
                        <line x1="12" y1="1.5" x2="12" y2="4.5" />
                        <line x1="12" y1="19.5" x2="12" y2="22.5" />
                        <line x1="1.5" y1="12" x2="4.5" y2="12" />
                        <line x1="19.5" y1="12" x2="22.5" y2="12" />
                        <line x1="4.2" y1="4.2" x2="6.3" y2="6.3" />
                        <line x1="17.7" y1="17.7" x2="19.8" y2="19.8" />
                        <line x1="4.2" y1="19.8" x2="6.3" y2="17.7" />
                        <line x1="17.7" y1="6.3" x2="19.8" y2="4.2" />
                      </g>
                    </svg>
                  ) : (
                    <svg
                      className="w-5 h-5 text-gray-800 dark:text-gray-200"
                      viewBox="0 0 24 24"
                      fill="none"
                      role="img"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <title>Moon — switch to dark mode</title>
                      <path
                        d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z"
                        fill="currentColor"
                        opacity="0.95"
                      />
                    </svg>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default LMSHeader;